import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../design-system/icons.jsx';
import { exportCSV, exportJSON, exportPDF } from '../../utils/export';
import ActiveFilterBar from './ActiveFilterBar';

export default function Toolbar({
  columns,
  onExport,
  loading = false,
  columnFilters,
  onClear,
  onClearAll,
  search,
  onSearch,
  filteredRows,
  exportFilename,
  exportColumns,
  exportOptions,
  toolbarLeft,
  toolbarRight,
}) {
  const { t } = useTranslation();
  const [searchVal,  setSearchVal]  = useState(search);
  const [showExport, setShowExport] = useState(false);
  const exportRef = useRef(null);
  const [exportBusy, setExportBusy] = useState(false);
  const [exportError, setExportError] = useState(null);

  useEffect(() => { setSearchVal(search); }, [search]);

  const searchTimer = useRef(null);
  useEffect(() => () => clearTimeout(searchTimer.current), []);
  useEffect(() => { clearTimeout(searchTimer.current); }, [search]);
  const changeSearch = (value) => {
    setSearchVal(value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => onSearch(value), 250);
  };

  useEffect(() => {
    if (!showExport) return;
    const handler = (e) => {
      if (!exportRef.current?.contains(e.target)) setShowExport(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showExport]);

  const expCols      = exportColumns || columns.map(c => ({ key: c.key, label: c.label }));
  const disabled     = loading || exportBusy || filteredRows.length === 0;
  const hasColFilter = Object.values(columnFilters).some(Boolean);
  const hasAnyFilter = hasColFilter || searchVal !== '';

  const runExport = async (format) => {
    setShowExport(false);
    setExportBusy(true);
    setExportError(null);
    try {
      if (onExport) await onExport(format);
      else if (format === 'csv') exportCSV(filteredRows, expCols, exportFilename, exportOptions || {});
      else if (format === 'json') exportJSON(filteredRows, exportFilename);
      else exportPDF(filteredRows, expCols, exportFilename, exportOptions || {});
    } catch (e) {
      setExportError(e?.response?.data?.detail || e.message);
    } finally { setExportBusy(false); }
  };

  const clearAll = () => {
    clearTimeout(searchTimer.current);
    onClearAll();
    setSearchVal('');
    onSearch('');
  };

  return (
    <div style={S.wrap}>
      {toolbarLeft && <div style={S.left}>{toolbarLeft}</div>}
      <div style={S.center}>
        <ActiveFilterBar
          columns={columns}
          columnFilters={columnFilters}
          onClear={onClear}
          onClearAll={onClearAll}
        />
      </div>

      <div style={S.right}>
        <div style={S.searchWrap}>
          <Icon name="search" size={13} strokeWidth={2} style={S.searchIcon} />
          <input
            type="search"
            value={searchVal}
            onChange={e => changeSearch(e.target.value)}
            aria-label={t('table.search')}
            placeholder={t('table.search')}
            style={S.searchInput}
          />
        </div>

        {hasAnyFilter && (
          <button onClick={clearAll} style={S.clearBtn} title={t('table.clearAllFilters')}>
            <Icon name="close" size={11} strokeWidth={2.5} />
            {t('table.clearAllFilters')}
          </button>
        )}

        <div ref={exportRef} style={{ position: 'relative' }}>
          <button
            onClick={() => !disabled && setShowExport(s => !s)}
            disabled={disabled}
            style={{ ...S.exportBtn, opacity: disabled ? 0.4 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
          >
            <Icon name="download" size={12} strokeWidth={2} />
            {exportBusy ? t('common.loading') : 'Export'}
          </button>
          {showExport && (
            <div style={S.dropdown}>
              <button style={S.dropItem} onClick={() => runExport('csv')}>
                Export CSV
              </button>
              <button style={S.dropItem} onClick={() => runExport('json')}>
                Export JSON
              </button>
              <button style={S.dropItem} onClick={() => runExport('pdf')}>
                Export PDF
              </button>
            </div>
          )}
        </div>

        {exportError && <span role="alert" className="text-danger small">{exportError}</span>}
        {toolbarRight}
      </div>
    </div>
  );
}

const S = {
  wrap: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '9px 16px',
    background: 'var(--bg-surface)',
    borderBottom: '1px solid var(--border-subtle)',
    flexWrap: 'wrap',
  },
  left:   { display: 'flex', alignItems: 'center', gap: 6, flexShrink: 1, minWidth: 0, maxWidth: '100%' },
  center: { flex: 1, display: 'flex', alignItems: 'center', minWidth: 0 },
  right:  { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', maxWidth: '100%' },
  searchWrap: {
    position: 'relative', display: 'flex', alignItems: 'center',
  },
  searchIcon: {
    position: 'absolute', left: 8, color: 'var(--text-muted)', pointerEvents: 'none',
  },
  searchInput: {
    paddingLeft: 28, paddingRight: 10, paddingTop: 5, paddingBottom: 5,
    width: 176,
    background: 'var(--bg-elevated)', border: '1px solid var(--border)',
    borderRadius: 6, color: 'var(--text-primary)', fontSize: 12,
    fontFamily: 'inherit',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  },
  clearBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '5px 10px',
    background: 'color-mix(in srgb, var(--threat-critical) 10%, transparent)',
    border: '1px solid var(--threat-critical)',
    borderRadius: 6, color: 'var(--threat-critical)',
    fontSize: 11, fontWeight: 600,
    fontFamily: 'inherit', cursor: 'pointer',
    letterSpacing: '0.02em',
    transition: 'background 0.12s, opacity 0.12s',
  },
  exportBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '5px 10px',
    background: 'transparent', border: '1px solid var(--border)',
    borderRadius: 6, color: 'var(--text-secondary)',
    fontSize: 12, fontFamily: 'inherit',
    transition: 'border-color 0.15s, color 0.15s',
  },
  dropdown: {
    position: 'absolute', top: '100%', right: 0, marginTop: 4,
    background: 'var(--bg-elevated)', border: '1px solid var(--border)',
    borderRadius: 8, boxShadow: 'var(--shadow-md)', zIndex: 9000,
    minWidth: 140, overflow: 'hidden',
  },
  dropItem: {
    display: 'block', width: '100%',
    padding: '8px 14px', textAlign: 'left',
    background: 'none', border: 'none',
    color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer',
    fontFamily: 'inherit',
  },
};
